import React, { useState, useEffect, useRef } from "react";
import { Search, X, Clock, TrendingUp, Filter } from "../utils/icons";
import { SearchService, SearchResult } from "../services/searchService";
import {
  EnhancedSearchService,
  getSearchHistory,
  getPopularSearches,
  trackResultClick,
} from "../services/enhancedSearchService";

interface SearchBoxProps {
  onSearch?: (query: string) => Promise<SearchResult[]>;
  onResultClick?: (result: SearchResult) => void;
  placeholder?: string;
  className?: string;
  showAdvancedFeatures?: boolean;
}

// Enhanced search function using the new service
const enhancedSearch = async (query: string): Promise<SearchResult[]> => {
  try {
    const searchResponse = await EnhancedSearchService.enhancedSearch(query, {
      limit: 10,
      types: ["blog", "course", "prompt"],
    });

    // Combine all results
    const allResults = [
      ...searchResponse.blog,
      ...searchResponse.courses,
      ...searchResponse.prompts,
    ];

    return allResults;
  } catch (error) {
    console.error("❌ Navigation search error:", error);
    // Fallback to basic search
    const fallbackResponse = await SearchService.searchAll(query, {
      limit: 10,
      types: ["blog", "course", "prompt"],
    });

    const fallbackResults = [
      ...fallbackResponse.blog,
      ...fallbackResponse.courses,
      ...fallbackResponse.prompts,
    ];

    return fallbackResults;
  }
};

function SearchBox({
  onSearch = enhancedSearch,
  onResultClick,
  placeholder = "Search blog posts, courses, and prompts...",
  className = "",
  showAdvancedFeatures = true,
}: SearchBoxProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchHistory, setSearchHistory] = useState<
    Array<{ query: string; timestamp: Date }>
  >([]);
  const [popularSearches, setPopularSearches] = useState<
    Array<{ query: string; count: number }>
  >([]);
  const [showHistory, setShowHistory] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Load search history and popular searches on mount
  useEffect(() => {
    if (showAdvancedFeatures) {
      setSearchHistory(getSearchHistory().slice(0, 5));
      setPopularSearches(getPopularSearches());
    }
  }, [showAdvancedFeatures]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowSuggestions(false);
        setShowHistory(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle search with debouncing
  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (query.trim() && query.length >= 2) {
        setLoading(true);
        try {
          const searchResults = await onSearch(query);
          setResults(searchResults);
          setIsOpen(true);
          setShowSuggestions(false);
          setShowHistory(false);
        } catch (error) {
          console.error("Search error:", error);
          setResults([]);
        } finally {
          setLoading(false);
        }
      } else {
        setResults([]);
        setIsOpen(false);
        if (query.length >= 2) {
          // Show suggestions when query is 2+ characters but no results
          const searchSuggestions =
            await SearchService.getSearchSuggestions(query);
          setSuggestions(searchSuggestions);
          setShowSuggestions(true);
          setShowHistory(false);
        } else {
          // Don't automatically show history when query is empty
          // History will only show when user focuses on the input
          setShowSuggestions(false);
          setShowHistory(false);
        }
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [query, onSearch]);

  const handleClose = () => {
    setQuery("");
    setIsOpen(false);
    setShowSuggestions(false);
    setShowHistory(false);
    setResults([]);
    setSuggestions([]);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    setShowHistory(false);
  };

  const handleHistoryClick = (historyQuery: string) => {
    setQuery(historyQuery);
    setShowHistory(false);
  };

  const handlePopularSearchClick = (popularQuery: string) => {
    setQuery(popularQuery);
    setShowHistory(false);
  };

  const handleResultClick = (result: SearchResult) => {
    // Track the click for analytics
    trackResultClick(result.id, query);
    setIsOpen(false);

    // Use custom click handler if provided, otherwise use default navigation
    if (onResultClick) {
      onResultClick(result);
    }
  };

  const renderSearchResult = (result: SearchResult) => (
    <a
      key={result.id}
      href={onResultClick ? undefined : result.url}
      className="block hover:bg-gray-50 p-4 transition-colors duration-200"
      onClick={(e) => {
        if (onResultClick) {
          e.preventDefault();
        }
        handleResultClick(result);
      }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4
            className="text-base font-medium text-gray-900"
            dangerouslySetInnerHTML={{
              __html: SearchService.highlightSearchTerms(result.title, query),
            }}
          />
          {result.description && (
            <p
              className="text-sm text-gray-600 mt-1 line-clamp-2"
              dangerouslySetInnerHTML={{
                __html: SearchService.highlightSearchTerms(
                  result.description,
                  query,
                ),
              }}
            />
          )}
          <div className="flex items-center mt-2 space-x-4 text-xs text-gray-500">
            <span className="inline-block font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {result.type}
            </span>
            {result.score && (
              <span className="text-green-600 font-medium">
                {Math.round(result.score)}% match
              </span>
            )}
            {result.published_at && (
              <span>{new Date(result.published_at).toLocaleDateString()}</span>
            )}
          </div>
        </div>
      </div>
    </a>
  );

  const renderSearchHistory = () => (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
      <div className="py-2">
        <h3 className="px-4 py-2 text-sm font-medium text-gray-500 bg-gray-50 flex items-center">
          <Clock className="h-4 w-4 mr-2" />
          Recent Searches
        </h3>
        {searchHistory.length > 0 ? (
          searchHistory.map((history, index) => (
            <button
              key={index}
              onClick={() => handleHistoryClick(history.query)}
              className="block w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors duration-200"
            >
              <span className="text-sm text-gray-700">{history.query}</span>
              <span className="text-xs text-gray-500 ml-2">
                {new Date(history.timestamp).toLocaleDateString()}
              </span>
            </button>
          ))
        ) : (
          <div className="px-4 py-2 text-sm text-gray-500">
            No recent searches
          </div>
        )}
      </div>
    </div>
  );

  const renderPopularSearches = () => (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
      <div className="py-2">
        <h3 className="px-4 py-2 text-sm font-medium text-gray-500 bg-gray-50 flex items-center">
          <TrendingUp className="h-4 w-4 mr-2" />
          Popular Searches
        </h3>
        {popularSearches.length > 0 ? (
          popularSearches.map((popular, index) => (
            <button
              key={index}
              onClick={() => handlePopularSearchClick(popular.query)}
              className="block w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors duration-200"
            >
              <span className="text-sm text-gray-700">{popular.query}</span>
              <span className="text-xs text-gray-500 ml-2">
                {popular.count} searches
              </span>
            </button>
          ))
        ) : (
          <div className="px-4 py-2 text-sm text-gray-500">
            No popular searches yet
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={`relative ${className}`} ref={searchRef}>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="w-full px-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F98B3D] focus:border-transparent"
          aria-label="Search"
          autoFocus={false}
          onFocus={() => {
            if (query.length === 0 && showAdvancedFeatures) {
              setShowHistory(true);
            }
          }}
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
        {query && (
          <button
            onClick={handleClose}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label="Clear search"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Search History */}
      {showHistory && showAdvancedFeatures && renderSearchHistory()}

      {/* Popular Searches */}
      {showHistory &&
        showAdvancedFeatures &&
        popularSearches.length > 0 &&
        renderPopularSearches()}

      {/* Search Results */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-[80vh] overflow-y-auto z-50">
          <div className="divide-y divide-gray-200">
            {/* Group results by type */}
            {["Blog Post", "Course", "Prompt"].map((type) => {
              const typeResults = results.filter(
                (result) => result.type === type,
              );
              if (typeResults.length === 0) return null;

              return (
                <div key={type} className="py-2">
                  <h3 className="px-4 py-2 text-sm font-medium text-gray-500 bg-gray-50 flex items-center">
                    <Filter className="h-4 w-4 mr-2" />
                    {type}s ({typeResults.length})
                  </h3>
                  {typeResults.map(renderSearchResult)}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search Suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="py-2">
            <h3 className="px-4 py-2 text-sm font-medium text-gray-500 bg-gray-50">
              Suggestions
            </h3>
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="block w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors duration-200"
              >
                <span className="text-sm text-gray-700">{suggestion}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading State */}
      {isOpen && loading && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#F98B3D] mx-auto"></div>
          <p className="text-sm text-gray-600 mt-2">Searching...</p>
        </div>
      )}

      {/* No Results */}
      {isOpen && !loading && results.length === 0 && query.trim() !== "" && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 text-center">
          <p className="text-gray-600">No results found for "{query}"</p>
          <p className="text-sm text-gray-500 mt-1">
            Try different keywords or check your spelling
          </p>
        </div>
      )}
    </div>
  );
}

export default React.memo(SearchBox);
